// Client side of the voice-to-estimate feature: send a transcript to the
// server route, get back structured lines, and shape them the way the rest of
// REKON already stores labor and parts.
//
// This file does NOT price anything. It produces hours and quantities; every
// dollar figure still comes from EstimatePanel.calcTotals via the estimate
// draft, so there is exactly one place where money math lives.

function makeId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function str(v) {
  return typeof v === 'string' ? v.trim() : '';
}

// The schema on the server guarantees the shape, but a 200 is not a promise
// that the content is usable — drop empty lines rather than writing junk onto
// a document a customer will read.
export function normalizeDraft(raw) {
  return {
    complaint:   str(raw?.complaint),
    cause:       str(raw?.cause),
    correction:  str(raw?.correction),
    category:    str(raw?.category) || null,
    customerExplanation: str(raw?.customerExplanation),
    laborLines: (Array.isArray(raw?.laborLines) ? raw.laborLines : [])
      .map(l => ({ description: str(l?.description), hours: num(l?.hours) }))
      .filter(l => l.description && l.hours > 0),
    parts: (Array.isArray(raw?.parts) ? raw.parts : [])
      .map(p => ({
        description: str(p?.description),
        quantity:    num(p?.quantity) || 1,
        partNumber:  str(p?.partNumber) || null,
      }))
      .filter(p => p.description),
    assumptions: (Array.isArray(raw?.assumptions) ? raw.assumptions : [])
      .map(str).filter(Boolean),
  };
}

export async function requestEstimateDraft({ transcript, unit, complaint }) {
  const res = await fetch('/api/ai-estimate', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ transcript, unit, complaint }),
  });

  const body = await res.text();
  const data = body ? JSON.parse(body) : {};

  if (!res.ok) {
    const message = typeof data?.error === 'string' ? data.error : data?.error?.message || '';
    if (res.status === 503) throw new Error('AI is not configured on this deployment.');
    if (message.includes('credit balance')) {
      throw new Error('The Anthropic account is out of credits (console.anthropic.com → Billing).');
    }
    throw new Error(message || 'Could not draft the estimate. Try again.');
  }

  return normalizeDraft(data?.draft);
}

// ── Mapping into REKON's stored shapes ───────────────────────────────────────

// Matches the labor line shape written by EstimatePanel.handleAddLabor and
// invoiceDraft.laborFromCategories — hours x rate, no flat pricing.
export function toLaborLines(draft, defaultLaborRate) {
  const rate = num(defaultLaborRate) || 95;
  return draft.laborLines.map(l => ({
    id:            makeId('lab'),
    description:   l.description,
    catalogItemId: null,
    hours:         l.hours,
    rate,
    flatPrice:     null,
    amount:        l.hours * rate,
    techName:      null,
    notes:         'Drafted from a voice note — review before sending',
  }));
}

// Matches the part shape written by ROPage.handleAddPart. Cost and price stay
// at zero on purpose: nobody should be quoting a part price the shop hasn't
// looked up, and a zero is visibly wrong in a way a guess is not.
export function toPartLines(draft, { addedBy, timestamp }) {
  return draft.parts.map(p => ({
    id:          makeId('pl'),
    description: p.description,
    partNumber:  p.partNumber,
    vendor:      null,
    quantity:    p.quantity,
    cost:        0,
    price:       0,
    status:      'pending',
    orderedAt:   null,
    receivedAt:  null,
    eta:         null,
    notes:       'Drafted from a voice note — confirm number and price',
    addedBy:     addedBy || null,
    addedAt:     timestamp,
  }));
}

// The text that lands on the Findings tab.
export function toFindingText(draft) {
  return [
    draft.complaint  && `Complaint: ${draft.complaint}`,
    draft.cause      && `Cause: ${draft.cause}`,
    draft.correction && `Correction: ${draft.correction}`,
  ].filter(Boolean).join('\n');
}
